import { differenceInMinutes } from 'date-fns';
import { isNil } from 'lodash-es';
import { CandidateMatchType, CaseMatchType, EmployeeAssignmentCandidateMatch } from '@/api/apis/resources';
import { TrialProgram } from '@/api/apis/facilities/trial-programs';
import { CSShiftTimeGroup, Unavailability } from '@/api/apis/resources/schedule';
import { formatDate } from '@/utils/helpers';
import { shiftTimeGroupNames } from '@/constants/schedule.constants';

export const perfect = (match: CandidateMatchType) => match === CandidateMatchType.Perfect;
export const medium = (match: CandidateMatchType) => match === CandidateMatchType.Medium;
export const miss = (match: CandidateMatchType) => match === CandidateMatchType.Miss;
export const beforeAfter = (value: number) => (value < 0 ? 'before' : 'after');

const absOrZero = (value?: number | null) => (value ? Math.abs(value) : 0);

const formatDuration = (minutes?: number) => {
  if (typeof minutes === 'undefined') return '';

  const days = Math.floor(minutes / (24 * 60));
  const hours = Math.floor((minutes % (24 * 60)) / 60);
  const mins = minutes % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (mins > 0 || parts.length === 0) parts.push(`${mins.toFixed(0)}min`);

  return parts.join(' ');
};

export const getUnavailabilitiesWarnings = (unavailabilities?: Unavailability[], orTime?: string) => {
  const result: string[] = [];
  if (!unavailabilities?.length || !orTime) return result;
  unavailabilities.forEach((u) => {
    const unavailabilityStartToOrMinutes = differenceInMinutes(orTime, u.from);
    const unavailabilityEndToOrMinutes = differenceInMinutes(orTime, u.to);
    const startToOrDuration = formatDuration(unavailabilityStartToOrMinutes);
    const endToOrDuration = formatDuration(unavailabilityEndToOrMinutes);
    result.push(`Unavailable hours start ${startToOrDuration} and end ${endToOrDuration} before OR.`);
  });
  return result;
};

export const getOrReservationConditions = (
  matchData: EmployeeAssignmentCandidateMatch,
  onCallBeforeOrMinutes?: number | null,
  onCallAfterOrMinutes?: number | null,
  onRestEndsBeforeOrMinutes?: number | null,
  unavailabilities?: Unavailability[],
  orTime?: string,
) => {
  if (isNil(onCallBeforeOrMinutes) && isNil(onCallAfterOrMinutes))
    return ['Resource has no On Call shift in 6 hours slot before case.'];
  const onCallBeforeOrDuration = formatDuration(absOrZero(onCallBeforeOrMinutes));
  const onCallAfterOrDuration = formatDuration(absOrZero(onCallAfterOrMinutes));
  const onRestBeforeOrDuration = formatDuration(absOrZero(onRestEndsBeforeOrMinutes));
  const { matchType, onCallMatchType, onRestMatchType, onCallAfterTargetMatchType, onCallBeforeTargetMatchType } =
    matchData;
  const warnings = [];

  if (perfect(matchType)) warnings.push(`On Call shift starts ${onCallBeforeOrDuration} before OR.`);

  if (perfect(matchType) && !isNil(onCallAfterOrMinutes))
    warnings.push(`On Call shift ends ${onCallAfterOrDuration} ${beforeAfter(onCallAfterOrMinutes)} OR.`);

  if (medium(matchType) && medium(onCallMatchType) && medium(onCallBeforeTargetMatchType))
    warnings.push(`On Call shift starts ${onCallBeforeOrDuration} before OR.`);

  if (medium(matchType) && medium(onCallMatchType) && medium(onCallAfterTargetMatchType))
    warnings.push(`On Call shift ends ${onCallAfterOrDuration} before OR.`);

  if (medium(matchType) && medium(onRestMatchType))
    warnings.push(`Rest Period ends ${onRestBeforeOrDuration} before OR.`);

  if (miss(matchType) && miss(onCallMatchType))
    warnings.push(
      'On Call shift starts less than 2 hours before OR. Reservation is not available due to insufficient time for deployment.',
    );

  if (miss(matchType) && miss(onRestMatchType))
    warnings.push(
      'Rest Period ends less than 2 hours before OR. Reservation is not available due to insufficient time for deployment.',
    );

  const unavailabilityWarnings = getUnavailabilitiesWarnings(unavailabilities, orTime);
  warnings.push(...unavailabilityWarnings);

  return warnings;
};

type TrialCertParams = {
  isClinicalSpecialist: boolean;
  csName?: string;
  trialProgram?: TrialProgram | null;
  hasTrialCertification: boolean;
};

export const getTrialCertificationReservationConditions = ({
  isClinicalSpecialist,
  csName,
  trialProgram,
  hasTrialCertification,
}: TrialCertParams): string[] => {
  if (!isClinicalSpecialist || !trialProgram || hasTrialCertification) return [];
  const subject = csName ? `Clinical Specialist ${csName}` : 'Clinical Specialist';

  return [`${subject}: No certification in the ${trialProgram.name} trial program.`];
};

export const getGoTimeReservationConditions = (
  matchData: EmployeeAssignmentCandidateMatch,
  onCallBeforeTargetMinutes?: number | null,
  onCallAfterTargetMinutes?: number | null,
  onRestStartsAfterTargetMinutes?: number | null,
  onRestEndsBeforeTargetMinutes?: number | null,
) => {
  const onCallBeforeGoTimeDuration = formatDuration(absOrZero(onCallBeforeTargetMinutes));
  const onCallAfterGoTimeDuration = formatDuration(absOrZero(onCallAfterTargetMinutes));
  const onRestStartsAfterGoTimeDuration = formatDuration(absOrZero(onRestStartsAfterTargetMinutes));
  const onRestEndsBeforeGoTimeDuration = formatDuration(absOrZero(onRestEndsBeforeTargetMinutes));
  const {
    matchType,
    onCallMatchType,
    onRestMatchType,
    onRestEndsBeforeTargetMatchType,
    onRestStartsAfterTargetMatchType,
    onCallAfterTargetMatchType,
    onCallBeforeTargetMatchType,
  } = matchData;
  const warnings = [];

  if (medium(matchType) && medium(onCallMatchType) && medium(onCallBeforeTargetMatchType))
    warnings.push(`On Call starts in ${onCallBeforeGoTimeDuration} before Go Time.`);

  if (medium(matchType) && medium(onCallMatchType) && medium(onCallAfterTargetMatchType))
    warnings.push(`On Call ends in ${onCallAfterGoTimeDuration} after Go Time.`);

  if (miss(matchType) && miss(onCallMatchType) && miss(onCallBeforeTargetMatchType))
    warnings.push(`On Call starts in ${onCallBeforeGoTimeDuration} after Go Time.`);

  if (miss(matchType) && miss(onCallMatchType) && miss(onCallAfterTargetMatchType))
    warnings.push(`On Call ends in ${onCallAfterGoTimeDuration} before Go Time.`);

  if (miss(matchType) && miss(onRestMatchType) && miss(onRestStartsAfterTargetMatchType))
    warnings.push(`Rest starts in ${onRestStartsAfterGoTimeDuration} after Go Time.`);

  if (miss(matchType) && miss(onRestMatchType) && miss(onRestEndsBeforeTargetMatchType))
    warnings.push(`Rest ends in ${onRestEndsBeforeGoTimeDuration} after Go Time.`);

  return warnings;
};

export const getReservationConditions = (
  match: EmployeeAssignmentCandidateMatch,
  onCallBeforeTargetMinutes?: number | null,
  onCallAfterTargetMinutes?: number | null,
  onRestStartsAfterTargetMinutes?: number | null,
  onRestEndsBeforeTargetMinutes?: number | null,
  unavailabilities?: Unavailability[],
  orTime?: string,
  applyGoTime?: boolean,
) => {
  if (applyGoTime) {
    return getGoTimeReservationConditions(
      match,
      onCallBeforeTargetMinutes,
      onCallAfterTargetMinutes,
      onRestStartsAfterTargetMinutes,
      onRestEndsBeforeTargetMinutes,
    );
  }
  return getOrReservationConditions(
    match,
    onCallBeforeTargetMinutes,
    onCallAfterTargetMinutes,
    onRestEndsBeforeTargetMinutes,
    unavailabilities,
    orTime,
  );
};

export const getReservationStatus = (caseMatchType: CaseMatchType) => {
  switch (caseMatchType) {
    case CaseMatchType.AssignedToAnotherCase:
    case CaseMatchType.AlreadyAssigned:
      return 'On Case';
    case CaseMatchType.ReservedForAnotherCase:
    case CaseMatchType.AlreadyReserved:
      return 'Reserved';
    default:
      return '';
  }
};

export const getShiftTimeGroupCondition = (shiftTimeGroup?: CSShiftTimeGroup | null): string[] => {
  if (isNil(shiftTimeGroup)) return [];
  return [`Shift Time Group ${shiftTimeGroupNames[shiftTimeGroup]}.`];
};

export const getTargetTimeLabel = (showGoTime?: boolean, targetTime?: string) => {
  const timeFormatted = formatDate(targetTime, 'MM/dd/yyyy HH:mm zzz');
  return `${showGoTime ? 'Go Time' : 'Or Time'} ${timeFormatted}.`;
};
